import { useCallback, useEffect, useMemo, useState } from "react";
import { MondayBoardSelector } from "frontend/canvas-designer-new/monday-import-items/monday-board-selector/monday-board-selector";
import {
  useMondayImportItems,
  type BoardWithColumns,
} from "frontend/canvas-designer-new/monday-import-items/monday-import.hook";
import { useBoardIntegrations } from "frontend/hooks/use-board-integrations";
import { useBoardValue } from "frontend/state/board-state";
import { MondayIntegrationType } from "shared/integrations/integration";
import ConfirmButton from "../../components/confirm-button";
import SyncedWidgetHeader from "../../components/synced-widget-header";
import { DependencyMappingSkeleton } from "./dependency-mapping-skeleton";
import { DependencyMappingLoader } from "./dependency-mapping-loader";
import styles from "./dependency-mapping.module.css";
import { AIBlockService } from "frontend/services/ai-blocks/ai-block-service";
import { boardDependencyMapBlock } from "frontend/services/ai-blocks/board-blocks/board-dependency-map-block";
import { BoardContext } from "shared/ai-model/board-context";
import { getDefaultColumnsMapping } from "utils/monday-utils";
import { animateCenterToElements } from "frontend/canvas-controls/zoom-controls/fit-to-stage";
import { stageRefAtom, layerRefAtom, utilsAtom, documentIdAtom, posScaleAtom } from "state-atoms";
import { useAtomValue, useSetAtom } from "jotai";
import Konva from "konva";
import { NewElementData } from "shared/datamodel/canvas-element";
import { Point } from "shared/datamodel/schemas";
import { ResourceType } from "frontend/canvas-designer-new/monday-import-items/monday-import-items.types";
import PreviewIcon from "./preview-icon";

function dependencyBoardFilter(board: BoardWithColumns): boolean {
  const columns = board.columns ?? [];
  return columns.some((col) => col.type === "dependency");
}

const aiBlockService = AIBlockService.getInstance();

export default function DependencyMapping({ onComplete }: { onComplete: (data: unknown) => void }) {
  const [{ board }] = useBoardValue();
  const { mondayIntegration, addBoardIntegration } = useBoardIntegrations(board?.documentId);

  const {
    workspaces,
    workspacesLoading,
    workspacesInitialLoading,
    loadMoreWorkspaces,
    selectedWorkspace,
    setSelectedWorkspace,
    boards,
    boardsLoading,
    boardsInitialLoading,
    selectedBoard,
    setSelectedBoard,
    loadMoreBoards,
  } = useMondayImportItems({
    withColumns: true,
    boardCompatibilityFilter: dependencyBoardFilter,
  });

  const [isCreating, setIsCreating] = useState(false);
  const documentId = useAtomValue(documentIdAtom).documentId;
  const canvasUtils = useAtomValue(utilsAtom(documentId));
  const stageRef = useAtomValue(stageRefAtom);
  const layerRef = useAtomValue(layerRefAtom);
  const setPosScale = useSetAtom(posScaleAtom);

  // Get columns from the selected board (fetched with withColumns: true)
  const columns = useMemo(() => {
    const boardColumns = (
      selectedBoard as {
        columns?: Array<{ id: string; type: string; title: string; settings_str?: string }>;
      }
    )?.columns;
    if (!boardColumns) return [];
    return boardColumns.map((c) => ({
      id: c.id,
      type: c.type,
      title: c.title,
      settings: c.settings_str ? JSON.parse(c.settings_str) : {},
    }));
  }, [selectedBoard]);

  const hasDependencyColumn = columns.some((col) => col.type === "dependency");

  const placeElements = useCallback(
    async (elements: Map<string, unknown> | NewElementData[], position: Point, metadata?: Record<string, unknown>) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newElementIds = await canvasUtils?.copyElements(elements as any, position, true);
      if (metadata) {
        canvasUtils?.addMetadata(metadata);
      }

      if (newElementIds && newElementIds.length > 0 && stageRef?.current && layerRef?.current) {
        setTimeout(() => {
          if (!layerRef?.current || !stageRef?.current) return;

          const newNodes = layerRef.current
            .find((node: unknown) => {
              const konvaNode = node as Konva.Node;
              return newElementIds.includes(konvaNode.attrs.id);
            })
            .toArray();
          if (newNodes.length > 0) {
            canvasUtils?.setSelectedElementIds(newElementIds);

            // Update stageState.selectedNodes so keyboard shortcuts work immediately
            canvasUtils?.enableTransformer();

            animateCenterToElements(
              stageRef,
              newNodes,
              (posScale) => {
                setPosScale(posScale);
              },
              0.15,
              { animationDuration: 200 }
            );
          }
        }, 100);
      }
    },
    [canvasUtils, stageRef, layerRef, setPosScale]
  );

  useEffect(() => {
    aiBlockService.registerBlock("board-dependency-map", boardDependencyMapBlock);
  }, []);

  const handleCreateDependencyMapping = useCallback(async () => {
    if (!selectedBoard || !mondayIntegration || isCreating || !hasDependencyColumn) {
      return;
    }

    setIsCreating(true);

    try {
      const mappedColumns = getDefaultColumnsMapping(columns, MondayIntegrationType.tasks);
      let subitemsBoardIntegrationId: string | undefined;

      const boardIntegrationId = await addBoardIntegration(mondayIntegration.id, {
        boardId: selectedBoard.id,
        type: MondayIntegrationType.tasks,
        columns: mappedColumns,
      });

      // Check if selectedBoard has subitem_board info
      const subitemBoard = (selectedBoard as { subitem_board?: { id: string } }).subitem_board;
      if (subitemBoard) {
        subitemsBoardIntegrationId = await addBoardIntegration(mondayIntegration.id, {
          boardId: subitemBoard.id,
          type: MondayIntegrationType.tasks,
          columns: mappedColumns,
        });
      }

      const boardContext: BoardContext = {
        integrationId: mondayIntegration.id,
        boardId: selectedBoard.id,
        boardName: selectedBoard.name,
        documentId: board?.documentId || "",
        boardIntegrationId,
        subitemsBoardIntegrationId,
      };

      const newPosition = { x: 100, y: 100 };
      const { elements, metadata } = await aiBlockService.generate("board-dependency-map", {
        prompt: "Create a dependency mapping for the selected board",
        position: newPosition,
        boardContext,
      });

      await placeElements(elements, newPosition, metadata);
      onComplete(null);
    } catch (error) {
      console.error("Error creating dependency mapping:", error);
      setIsCreating(false);
    }
  }, [
    selectedBoard,
    mondayIntegration,
    isCreating,
    hasDependencyColumn,
    columns,
    addBoardIntegration,
    board?.documentId,
    placeElements,
    onComplete,
  ]);

  const isCreateDisabled =
    !selectedBoard || !selectedWorkspace || !mondayIntegration || isCreating || !hasDependencyColumn;

  if (workspacesInitialLoading) {
    return <DependencyMappingSkeleton />;
  }

  if (isCreating) {
    return (
      <div className={styles.container}>
        <DependencyMappingLoader />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.previewImageContainer}>
          <PreviewIcon />
        </div>
      </div>
      <div className={styles.contentContainer}>
        <div className={styles.selectorsRow}>
          <SyncedWidgetHeader
            title="Show how tasks or systems link"
            subtitle="Select a board with a Dependency column"
          />
        </div>
        <div className={styles.selectorsRow}>
          <MondayBoardSelector
            workspaces={workspaces}
            selectedWorkspace={selectedWorkspace}
            setSelectedWorkspace={setSelectedWorkspace}
            workspacesLoading={workspacesLoading}
            workspacesInitialLoading={workspacesInitialLoading}
            loadMoreWorkspaces={loadMoreWorkspaces}
            boards={boards}
            selectedBoard={selectedBoard}
            setSelectedBoard={(pickedBoard: ResourceType) => {
              const fullBoard = boards.find((b) => b.id === pickedBoard.id);
              setSelectedBoard(fullBoard ?? null);
            }}
            boardsLoading={boardsLoading}
            boardsInitialLoading={boardsInitialLoading}
            loadMoreBoards={loadMoreBoards}
            workspacePickerTitle="Select workspace"
            boardPickerTitle="Select board"
            isBoardDisabled={(board) => {
              const fullBoard = boards.find((b) => b.id === board?.id);
              return fullBoard ? !fullBoard.isCompatible : false;
            }}
            disabledBoardTooltip="Doesn't have a Dependency column"
            pickerWidth="158px"
            pickerStyle={{
              color: "#1E1E1E",
              fontSize: "10px",
              fontWeight: 400,
              marginBottom: "4px",
            }}
            useFloatingDropdown={true}
          />
        </div>
      </div>

      <div className={styles.createButtonContainer}>
        <ConfirmButton
          title={isCreating ? "Creating..." : "Create"}
          disabled={isCreateDisabled}
          onClick={handleCreateDependencyMapping}
          trackingEventName="cim_dependency_mapping_created"
        />
      </div>
    </div>
  );
}
